import React, { useState, useEffect, useMemo } from 'react';

import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Lightbox } from '@shared/components/helpers/lightbox';
import { Pagination } from '@shared/components/pagination';

import { useMessagesData } from './hooks/use_messages_data';
import { usePhoneSetup } from './hooks/use_phone_setup';
import { usePlaceholder } from './hooks/use_placeholder';
import { MessageForm } from './message_form';
import { MessageList } from './message_list';
import { MessagesWidgetProps, PhoneWithName } from './types';

export const MessagesWidget: React.FC<MessagesWidgetProps> = ({ recipient, recipientType, order }) => {
  const {
    messageBody,
    setMessageBody,
    phoneList,
    setPhoneList,
    phoneToSendTo,
    setPhoneToSendTo,
    phoneToFilterBy,
    setPhoneToFilterBy,
    smsEnabled,
    loadingState,
    error,
    setError,
    filteredMessages,
    loadMessages,
    sendMessage,
    resolveMessageThread,
    loadPreferences,
    recipientHasPhone,
    messagesData,
  } = useMessagesData(recipient, recipientType, order);

  usePhoneSetup(recipient, recipientType, order, setPhoneList, setPhoneToSendTo, loadMessages);

  const placeholder = usePlaceholder(smsEnabled, recipientType, recipientHasPhone, loadingState);
  const [lightboxImage, setLightboxImage] = useState<string | undefined>(undefined);

  // Load SMS preferences when phone changes
  useEffect(() => {
    if (phoneToSendTo || (recipientType === 'Lead' && recipient.id)) {
      loadPreferences();
    }
  }, [loadPreferences, phoneToSendTo, recipientType, recipient.id]);

  const hasMultiplePhones = useMemo(() => phoneList.length > 1, [phoneList.length]);

  const handleFilterChange = (selectedPhone: PhoneWithName | undefined) => {
    setPhoneToFilterBy(selectedPhone);
    if (selectedPhone) {
      setPhoneToSendTo(selectedPhone);
    }
  };

  const paginationWithNavigation = useMemo(() => {
    const paginationData = messagesData?.paginatedMessages?.pagination || {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
    };
    return {
      ...paginationData,
      nextPage: paginationData.currentPage < paginationData.totalPages ? paginationData.currentPage + 1 : null,
      prevPage: paginationData.currentPage > 1 ? paginationData.currentPage - 1 : null,
    };
  }, [messagesData?.paginatedMessages?.pagination]);

  return (
    <div className={`panel sms-panel ${!smsEnabled ? 'inactive' : ''}`} id="messages-component">
      <div className="panel-heading">
        <h3 className="panel-title">
          SMS
          <button
            className="btn btn-refresh pull-right"
            onClick={() => loadMessages(1)}
            disabled={loadingState.messages}
          >
            <FontAwesomeIcon icon={faRefresh} size="2x" spin={loadingState.messages} />
          </button>
        </h3>
      </div>
      <div className="panel-body">
        {error && (
          <div className="alert alert-danger" role="alert">
            {error.message}
            <button type="button" className="close" onClick={() => setError(undefined)} aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        )}

        <div id="messages-container">
          <MessageForm
            messageBody={messageBody}
            setMessageBody={setMessageBody}
            smsEnabled={smsEnabled}
            placeholder={placeholder}
            loadingState={loadingState}
            phoneList={phoneList}
            phoneToSendTo={phoneToSendTo}
            setPhoneToSendTo={setPhoneToSendTo}
            hasMultiplePhones={hasMultiplePhones}
            recipientType={recipientType}
            onSendMessage={sendMessage}
          />

          <MessageList
            messages={filteredMessages}
            phoneList={phoneList}
            phoneToFilterBy={phoneToFilterBy}
            hasMultiplePhones={hasMultiplePhones}
            onFilterChange={handleFilterChange}
            onImageClick={setLightboxImage}
            onResolveThread={resolveMessageThread}
            loadingState={loadingState}
          />

          {paginationWithNavigation.totalPages > 1 && (
            <Pagination pagination={paginationWithNavigation} onPage={loadMessages} />
          )}
        </div>
      </div>

      {lightboxImage && (
        <Lightbox imageURL={lightboxImage} caption="Message Attachment" onClose={() => setLightboxImage(undefined)} />
      )}
    </div>
  );
};
